/**
 * BrandWordmark - Phase 84
 *
 * Single source of truth for the "Build Your Book™" wordmark across
 * the app. Renders the brand name with a small superscript "TM" in
 * the top-right corner, matching Indian trademark convention and the
 * visual treatment used by top-tier consumer brands (Notion™, Figma™,
 * etc.). One component → consistent kerning, font, and trademark
 * styling everywhere it appears.
 *
 * Why a component (vs `&trade;` everywhere): the trademark mark sits
 * at a SPECIFIC offset relative to the brand wordmark - superscript,
 * slightly raised, ~50% size, soft brown tone (#A68461) - and
 * inconsistent inlining would scatter that styling across 17+ files.
 * A single component prevents the brand text from drifting visually.
 *
 * Props:
 *   • size: tailwind text-size class (default 'text-2xl')
 *   • color: hex string or CSS variable (default '#2D3748')
 *   • tmColor: trademark mark colour (default '#A68461' - soft brown)
 *   • className: extra container classes
 *   • as: HTML tag (default 'span', use 'h1' for hero, etc.)
 *   • showTm: optionally hide the TM (set false on small chrome where
 *     space is tight - e.g. mobile reader header)
 *
 * Usage:
 *   <BrandWordmark size="text-3xl" />
 *   <BrandWordmark size="text-base" color="white" as="h1" />
 */
import { forwardRef } from 'react';

export const BrandWordmark = forwardRef(function BrandWordmark(
  {
    size = 'text-2xl',
    color = '#2D3748',
    tmColor = '#A68461',
    className = '',
    style: extraStyle,
    as: As = 'span',
    showTm = true,
    'data-testid': testId = 'brand-wordmark',
    ...rest
  },
  ref,
) {
  return (
    <As
      ref={ref}
      className={`inline-flex items-start font-semibold ${size} ${className}`}
      style={{ fontFamily: 'Fredoka', color, lineHeight: 1.1, ...extraStyle }}
      data-testid={testId}
      {...rest}
    >
      <span>Build Your Book</span>
      {showTm && (
        <sup
          aria-label="trademark"
          className="ml-0.5 font-medium"
          style={{
            // Superscript "TM" sized ~45% of the wordmark and raised
            // ~0.5em so it sits cleanly above the cap-height of the
            // last "k" in "Book". Soft brown so it reads as an
            // intentional mark, not a typo or footnote artefact.
            color: tmColor,
            fontSize: '0.45em',
            lineHeight: 1,
            transform: 'translateY(-0.15em)',
            // Bold-weighted because Fredoka's lighter weights look
            // anaemic at this small a size.
            fontWeight: 600,
            letterSpacing: '0.02em',
            // Keep the TM as a single visual block - no awkward wrap.
            whiteSpace: 'nowrap',
          }}
        >
          TM
        </sup>
      )}
    </As>
  );
});

export default BrandWordmark;
